"use client"

import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Link, useRouter, useT } from "../lib/router"
import { coreServices, services } from "../content/services"
import { solutions } from "../content/catalog"
import { Container, Icon, LanguageSwitcher, Wordmark, SITE } from "./ui"
import { SocialLinks } from "./social-links"

const NAV = [
  { to: "/services", label: { en: "Services", ar: "الخدمات" }, menu: true },
  { to: "/solutions", label: { en: "Solutions", ar: "الحلول" } },
  { to: "/work", label: { en: "Our Work", ar: "أعمالنا" } },
  { to: "/partners", label: { en: "Partners", ar: "الشركاء" } },
  { to: "/about", label: { en: "About", ar: "عن أوركست" } },
  { to: "/insights", label: { en: "Insights", ar: "رؤى" } },
  { to: "/careers", label: { en: "Careers", ar: "الوظائف" } },
]

export function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null)
  const { route } = useRouter()

  useEffect(() => {
    let frameId: number | null = null

    const update = () => {
      const el = barRef.current
      if (!el) return
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight
      const progress =
        scrollHeight > 0
          ? Math.min(Math.max(window.scrollY / scrollHeight, 0), 1)
          : 0
      el.style.transform = `scaleX(${progress})`
      frameId = null
    }

    const onScroll = () => {
      if (frameId === null) {
        frameId = window.requestAnimationFrame(update)
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(onScroll)
        : null

    if (resizeObserver) {
      resizeObserver.observe(document.documentElement)
    }

    update()

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      resizeObserver?.disconnect()
    }
  }, [route.path])

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed top-0 inset-x-0 z-[60] h-[3px] bg-transparent"
    >
      <div
        ref={barRef}
        className="h-full w-full bg-blue origin-left rtl:origin-right transition-transform duration-150 ease-out will-change-transform shadow-[0_0_8px_rgba(2,52,147,0.45)]"
        style={{ transform: "scaleX(0)" }}
      />
    </div>
  )
}

export function Header() {
  const t = useT()
  const { route } = useRouter()
  const [open, setOpen] = useState(false)
  const [menu, setMenu] = useState(false)
  const menuRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    setOpen(false)
    setMenu(false)
  }, [route.path, route.locale])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && (setOpen(false), setMenu(false))
    const onClick = (e: MouseEvent) =>
      menuRef.current &&
      !menuRef.current.contains(e.target as Node) &&
      setMenu(false)
    document.addEventListener("keydown", onKey)
    document.addEventListener("click", onClick)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("click", onClick)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    const opener = document.activeElement as HTMLElement | null
    document.body.style.overflow = "hidden"
    const dialog = document.getElementById("mobile-nav")
    dialog?.querySelector<HTMLElement>("button")?.focus()
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return
      const controls = dialog?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex="0"]',
      )
      if (!controls?.length) return
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", trapFocus)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("keydown", trapFocus)
      opener?.focus()
    }
  }, [open])

  const active = (to: string) =>
    route.path === to || route.path.startsWith(to + "/")

  return (
    <>
      <ScrollProgress />
      <header id="page-top" tabIndex={-1} className="sticky top-0 z-50 border-b border-line bg-white outline-none">
        <a
          href="#main"
          className="skip-link rounded-r8 bg-blue px-4 py-2 text-white"
        >
          {t({ en: "Skip to content", ar: "انتقل إلى المحتوى" })}
        </a>
        <Container className="flex h-[72px] items-center justify-between gap-6">
          <Link
            to="/"
            aria-label={t({ en: "ORKEST home", ar: "الصفحة الرئيسية لأوركست" })}
            className="shrink-0"
          >
            <Wordmark height={22} className="h-[22px] w-auto" />
          </Link>

          <nav
            aria-label={t({ en: "Main", ar: "القائمة الرئيسية" })}
            className="hidden xl:block"
          >
            <ul className="flex items-center gap-1">
              {NAV.map((n) =>
                n.menu ? (
                  <li key={n.to} className="relative" ref={menuRef}>
                    <button
                      type="button"
                      aria-expanded={menu}
                      aria-controls="services-menu"
                      onClick={() => setMenu((m) => !m)}
                      className={`inline-flex min-h-10 items-center gap-1 rounded-r8 px-3 text-base font-medium hover:text-blue ${
                        active(n.to) ? "text-blue" : "text-ink"
                      }`}
                    >
                      {t(n.label)}
                      <Icon
                        name="chevron-down"
                        className={`size-4 transition-transform ${menu ? "rotate-180" : ""}`}
                      />
                    </button>
                    {menu && (
                      <div
                        id="services-menu"
                        className="absolute start-0 top-full mt-3 w-[720px] rounded-r16 border border-line bg-white p-3 shadow-[0_24px_48px_-24px_rgba(23,35,51,0.25)] rise"
                      >
                        <div className="grid grid-cols-2 gap-1">
                          {services.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              className="flex gap-4 rounded-r8 p-3 hover:bg-mist"
                            >
                              <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-r8 bg-ice text-blue">
                                <Icon name={s.icon} className="size-5" />
                              </span>
                              <span>
                                <span className="block text-base font-medium text-ink">
                                  {t(s.title)}
                                </span>
                                <span className="mt-0.5 line-clamp-2 block text-sm leading-5 text-slate">
                                  {t(s.summary)}
                                </span>
                              </span>
                            </Link>
                          ))}
                        </div>
                        <Link
                          to="/services"
                          className="mt-2 flex items-center justify-between rounded-r8 bg-mist px-4 py-3 text-base font-medium text-blue"
                        >
                          {t({ en: "All services", ar: "جميع الخدمات" })}
                          <Icon name="arrow" flip className="size-5" />
                        </Link>
                      </div>
                    )}
                  </li>
                ) : (
                  <li key={n.to}>
                    <Link
                      to={n.to}
                      aria-current={active(n.to) ? "page" : undefined}
                      className={`inline-flex min-h-10 items-center rounded-r8 px-3 text-base font-medium hover:text-blue ${
                        active(n.to) ? "text-blue" : "text-ink"
                      }`}
                    >
                      {t(n.label)}
                    </Link>
                  </li>
                ),
              )}
            </ul>
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher className="inline-flex" />
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-expanded={open}
              aria-controls="mobile-nav"
              className="inline-flex size-11 items-center justify-center rounded-r8 ring-1 ring-slate xl:hidden"
            >
              <Icon name="menu" />
              <span className="sr-only">
                {t({ en: "Open menu", ar: "فتح القائمة" })}
              </span>
            </button>
          </div>
        </Container>

        {open && createPortal(
          <div
            id="mobile-nav"
            role="dialog"
            aria-modal="true"
            aria-label={t({ en: "Menu", ar: "القائمة" })}
            className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-white xl:hidden"
          >
            <Container className="flex h-[72px] shrink-0 items-center justify-between border-b border-line">
              <Wordmark height={22} className="h-[22px] w-auto" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex size-11 items-center justify-center rounded-r8 ring-1 ring-slate"
              >
                <Icon name="close" />
                <span className="sr-only">
                  {t({ en: "Close menu", ar: "إغلاق القائمة" })}
                </span>
              </button>
            </Container>
            <Container className="flex-1 py-6">
              <nav aria-label={t({ en: "Mobile", ar: "قائمة الجوال" })}>
                <ul className="divide-y divide-line">
                  {NAV.map((n) => (
                    <li key={n.to}>
                      <Link
                        to={n.to}
                        className="flex min-h-14 items-center justify-between text-xl font-medium text-ink"
                      >
                        {t(n.label)}
                        <Icon name="arrow" flip className="size-5 text-slate" />
                      </Link>
                      {n.menu && (
                        <ul className="grid gap-1 pb-4">
                          {services.map((s) => (
                            <li key={s.slug}>
                              <Link
                                to={`/services/${s.slug}`}
                                className="flex min-h-11 items-center gap-3 text-base text-slate"
                              >
                                <Icon
                                  name={s.icon}
                                  className="size-5 text-blue"
                                />
                                {t(s.title)}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                  <li>
                    <Link
                      to="/contact"
                      className="flex min-h-14 items-center text-xl font-medium text-ink"
                    >
                      {t({ en: "Contact", ar: "تواصل معنا" })}
                    </Link>
                  </li>
                </ul>
              </nav>
              <div className="mt-8 flex flex-col gap-3">
                <LanguageSwitcher className="justify-center py-2" />
              </div>
            </Container>
          </div>,
          document.body,
        )}
      </header>
    </>
  )
}

export function Footer() {
  const t = useT()
  const cols = [
    {
      title: { en: "Services", ar: "الخدمات" },
      links: [
        ...coreServices.map((s) => ({
          to: `/services/${s.slug}`,
          label: s.title,
        })),
        {
          to: "/services/cloud-devops",
          label: { en: "Hosting & Maintenance", ar: "الاستضافة والصيانة" },
        },
      ],
    },
    {
      title: { en: "Solutions", ar: "الحلول" },
      links: solutions.map((s) => ({
        to: `/solutions/${s.slug}`,
        label: s.title,
      })),
    },
    {
      title: { en: "Company", ar: "الشركة" },
      links: [
        { to: "/about", label: { en: "About ORKEST", ar: "عن أوركست" } },
        { to: "/partners", label: { en: "Partners", ar: "الشركاء" } },
        { to: "/work", label: { en: "System Models", ar: "نماذج الأنظمة" } },
        { to: "/careers", label: { en: "Careers", ar: "الوظائف" } },
        { to: "/contact", label: { en: "Contact", ar: "تواصل معنا" } },
        {
          to: "/privacy",
          label: { en: "Privacy policy", ar: "سياسة الخصوصية" },
        },
      ],
    },
  ]
  return (
    <footer className="bg-ink text-white">
      <Container className="py-16 md:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Wordmark variant="white" className="h-6 w-auto" />
            <p className="mt-6 text-base font-medium">{t(SITE.name)}</p>
            <p className="mt-3 max-w-sm text-base leading-7 text-white">
              {t({
                en: "Digital solutions to reach your customers, organize your business and simplify daily work.",
                ar: "حلول رقمية للوصول إلى عملائك، وتنظيم أعمالك، وتسهيل العمل اليومي.",
              })}
            </p>
            <ul className="mt-8 space-y-3 text-base text-white">
              <li className="flex items-center gap-3">
                <Icon name="mail" className="size-5 text-white" />
                <a
                  href={`mailto:${SITE.email}`}
                  className="hover:text-white"
                  style={{ direction: "ltr" }}
                >
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Icon name="pin" className="size-5 text-white" />
                {t(SITE.location)}
              </li>
            </ul>
            <SocialLinks />
          </div>
          {cols.map((c) => (
            <div
              key={c.title.en}
              className="lg:col-span-2 lg:[&:nth-child(2)]:col-span-3 lg:[&:nth-child(2)]:col-start-6"
            >
              <h2 className="text-sm font-medium uppercase tracking-[0.12em] text-white rtl:tracking-normal">
                {t(c.title)}
              </h2>
              <ul className="mt-5 space-y-3">
                {c.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="text-base text-white hover:underline underline-offset-4"
                    >
                      {t(l.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-16 border-t border-white/10 pt-8 text-sm text-white">
          <p>
            © {new Date().getFullYear()} {t(SITE.name)}
          </p>
        </div>
      </Container>
    </footer>
  )
}
